import * as React from 'react';
import {
  Button,
  DetailField,
  Divider,
  firstLine,
  sentenceCase,
  Stack,
  Text,
} from '@relay-towing/ui';
import { CreditCard, PhoneCall, Receipt, Route } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { openExternal } from '../../lib/nativeBridge';
import { dayAndTime, restOfAddress } from '../status';
import { CallActivity } from './CallActivity';
import { CallNotes } from './CallNotes';
import { CallPhotos } from './CallPhotos';
import { CallUrgently } from './CallUrgently';
import { CallVehicle } from './CallVehicle';
import { CreateInvoiceSheet } from './CreateInvoiceSheet';
import { PlaceSheet } from './PlaceSheet';
import type { Adder } from '../shell/adder';
import type { CallAddition } from './CallAddSheet';
import type { CallRecord } from './callRecord';

export type CallAdders = Record<CallAddition, React.RefObject<Adder>>;

/**
 * What a call says, the same in its sheet over the map and on its page when
 * there is no map, so the two cannot drift: the street heading it with its
 * town under it and, for the office, the Call activity button, then the
 * call's details and photos, its vehicle, its notes, and its invoice for a
 * member who may bill it, each a section ruled off from the next. The Figma's two call
 * frames (344:65534 and 344:65042) draw the same sections.
 */
export function CallDetailContent({
  call,
  editable,
  cancels,
  createsInvoice,
  invoiceAction,
  showsActivity,
  adders,
}: {
  call: CallRecord;
  /** May add photos, the vehicle and notes; the server refuses anyone else. */
  editable: boolean;
  /** May cancel an Urgently job or report it gone on arrival. */
  cancels: boolean;
  createsInvoice: boolean;
  /**
   * The invoice made: take its payment, or see it without the key to take
   * one. Null hides the button.
   */
  invoiceAction: 'take' | 'view' | null;
  /** The Call activity button, for a member with an office role. */
  showsActivity: boolean;
  /** Where the call's Add reaches each section's own add sheet. */
  adders?: CallAdders;
}) {
  const navigate = useNavigate();
  const [invoicing, setInvoicing] = React.useState(false);
  const [placing, setPlacing] = React.useState(false);
  const vehicle = call.vehicle;
  const town = restOfAddress(call.pickupLocation);

  return (
    // Each rule its own row, 24px either side, as the Figma's content column
    // draws its Dividers (591:22463). Stack divided borders the next section,
    // so its gap fell above the rule and the heading sat on the line.
    <Stack gap={6}>
      <Stack gap={4} align="start">
        <Stack gap={1}>
          <Text as="h1" variant="h1">
            {firstLine(call.pickupLocation)}
          </Text>
          {town ? <Text tone="muted">{town}</Text> : null}
        </Stack>
        {showsActivity && vehicle?.id ? (
          <CallActivity vehicleId={vehicle.id} />
        ) : null}
      </Stack>
      <Divider />
      <Stack gap={4}>
        <Text as="h2" variant="h2">
          Details
        </Text>
        <DetailField
          label="Type"
          value={call.towType ? sentenceCase(call.towType) : undefined}
        />
        {/* The name and the number each on its own row, the call button
            beside the number it dials. With both on one row the name hid
            the number, and the button dialled one nobody could see. */}
        {call.contactName ? (
          <DetailField label="Customer" value={call.contactName} />
        ) : null}
        {call.contactPhone ? (
          <DetailField
            label="Phone"
            value={call.contactPhone}
            action={
              <Button
                variant="secondary"
                size="icon-lg"
                pill
                aria-label={`Call ${call.contactPhone}`}
                onClick={() => openExternal(`tel:${call.contactPhone}`)}
              >
                <PhoneCall />
              </Button>
            }
          />
        ) : null}
        {call.accountName ? (
          <DetailField label="Account" value={call.accountName} />
        ) : null}
        <DetailField label="Priority" value={sentenceCase(call.priority)} />
        <DetailField
          label="Pickup Location"
          value={call.pickupLocation}
          action={
            <Button
              variant="secondary"
              size="icon-lg"
              pill
              aria-label="Open in maps"
              onClick={() => setPlacing(true)}
            >
              <Route />
            </Button>
          }
        />
        <PlaceSheet
          title="Pickup location"
          place={{
            address: call.pickupLocation,
            lat: call.pickupLocationLat,
            lng: call.pickupLocationLong,
          }}
          open={placing}
          onOpenChange={setPlacing}
        />
        {call.destination ? (
          <DetailField label="Dropoff" value={call.destination} />
        ) : null}
        {call.startedAt ? (
          <DetailField label="Dispatched" value={dayAndTime(call.startedAt)} />
        ) : null}
        {call.notes ? (
          <DetailField label="Dispatch notes" value={call.notes} />
        ) : null}
        {vehicle?.id ? (
          <CallPhotos
            ref={adders?.photos}
            vehicleId={vehicle.id}
            editable={editable}
          />
        ) : null}
      </Stack>
      {call.source === 'URGENTLY' ? (
        <CallUrgently call={call} editable={editable} cancels={cancels} />
      ) : null}
      <Divider />
      <Stack gap={4}>
        <Text as="h2" variant="h2">
          Vehicle
        </Text>
        <CallVehicle
          ref={adders?.vehicle}
          vehicle={vehicle}
          editable={editable}
        />
      </Stack>
      {vehicle?.id ? (
        <>
          <Divider />
          <CallNotes
            ref={adders?.note}
            vehicleId={vehicle.id}
            editable={editable}
          />
        </>
      ) : null}
      {createsInvoice || invoiceAction ? (
        <>
          <Divider />
          <Stack gap={4}>
            <Text as="h2" variant="h2">
              Invoice
            </Text>
            {createsInvoice ? (
              <>
                <Button
                  variant="outline"
                  leadingIcon={<Receipt />}
                  onClick={() => setInvoicing(true)}
                >
                  Create invoice
                </Button>
                <CreateInvoiceSheet
                  call={call}
                  open={invoicing}
                  onOpenChange={setInvoicing}
                />
              </>
            ) : null}
            {invoiceAction ? (
              <Button
                variant="outline"
                leadingIcon={<CreditCard />}
                onClick={() => navigate(`/calls/${call.id}/payment`)}
              >
                {invoiceAction === 'take' ? 'Take payment' : 'View invoice'}
              </Button>
            ) : null}
          </Stack>
        </>
      ) : null}
    </Stack>
  );
}
